import { Alert, Checkbox, Code, Group, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type {
  AutomationTestFixtureInputCandidate,
  AutomationTestFixtureInputIneligibility,
  AutomationTestFixtureTriggerCandidate,
  AutomationTestFixtureTriggerIneligibility,
} from '@/types/automations.ts';
import {
  appendSyntheticInputOverrideTemplate,
  appendSyntheticTriggerOverrideTemplate,
} from './syntheticFixtureAuthoring.ts';

type Props = {
  allowConfiguredInputs?: boolean;
  candidates: AutomationTestFixtureInputCandidate[];
  ineligibleInputs: AutomationTestFixtureInputIneligibility[];
  triggerCandidates: AutomationTestFixtureTriggerCandidate[];
  ineligibleTriggerInputs: AutomationTestFixtureTriggerIneligibility[];
  isLoading: boolean;
  error: unknown;
  inputOverridesValue: string;
  onInputOverridesChange: (value: string) => void;
  triggerOverridesValue: string;
  onTriggerOverridesChange: (value: string) => void;
  confirmedNonSensitive: boolean;
  onConfirmedNonSensitiveChange: (confirmed: boolean) => void;
};

export default function SyntheticFixtureAuthoringForm({
  allowConfiguredInputs = true,
  candidates,
  ineligibleInputs,
  triggerCandidates,
  ineligibleTriggerInputs,
  isLoading,
  error,
  inputOverridesValue,
  onInputOverridesChange,
  triggerOverridesValue,
  onTriggerOverridesChange,
  confirmedNonSensitive,
  onConfirmedNonSensitiveChange,
}: Props) {
  const appendTemplate = (
    payloadKind: 'schema_validated' | 'opaque_json' | 'producer_validated',
    candidate: AutomationTestFixtureInputCandidate,
  ) => {
    try {
      onInputOverridesChange(
        appendSyntheticInputOverrideTemplate(inputOverridesValue, candidate, payloadKind),
      );
    } catch (appendError) {
      notifications.show({
        color: 'red',
        title: 'Invalid synthetic inputs',
        message: appendError instanceof Error
          ? appendError.message
          : 'Fix the current JSON before adding another slot.',
      });
    }
  };

  const appendTriggerTemplate = (candidate: AutomationTestFixtureTriggerCandidate) => {
    try {
      onTriggerOverridesChange(
        appendSyntheticTriggerOverrideTemplate(triggerOverridesValue, candidate),
      );
    } catch (appendError) {
      notifications.show({
        color: 'red',
        title: 'Invalid Manual Trigger overrides',
        message: appendError instanceof Error
          ? appendError.message
          : 'Fix the current JSON before adding another trigger input.',
      });
    }
  };

  return <Stack gap="xs">
    <Alert color="blue" title="Exact, version-bound input slots">
      {allowConfiguredInputs
        ? 'Synthetic values replace exact configured JSON or tabular inputs, or schema-backed Manual Trigger ports during sealed safe-test execution.'
        : 'This source-free fixture supplies one complete schema-backed Manual Trigger JSON bundle during sealed safe-test execution.'}
      {' '}After creation, values are retained server-side; plans and evidence expose selectors, hashes, and byte counts only.
    </Alert>
    {isLoading && <Text size="xs" c="dimmed">Loading eligible inputs…</Text>}
    {Boolean(error) && (
      <Alert color="red" title="Could not load eligible inputs">{getHttpErrorMessage(error)}</Alert>
    )}
    {allowConfiguredInputs && candidates.map((candidate) => (
      <Group key={`${candidate.runtime_step_id}:${candidate.input_group_key}:${candidate.input_index}`} justify="space-between">
        <Text size="xs" c="dimmed">
          {candidate.step_name} · <Code>{candidate.input_group_key}[{candidate.input_index}]</Code>
          {' · '}source <Code>{candidate.source_step_id}</Code>
          {candidate.schema_id && candidate.schema_version != null
            ? <> · schema <Code>{candidate.schema_id} v{candidate.schema_version}</Code></>
            : candidate.validation_kind === 'schema' ? ' · producer schema validated'
              : candidate.validation_kind === 'representation' ? ' · representation validated'
                : ' · no declared schema'}
          {candidate.dataset_type === 'dataframe' ? ' · tabular array' : ' · JSON object'}
        </Text>
        <Group gap="xs">
          {candidate.schema_id && candidate.schema_version != null
            && (!candidate.supported_payload_kinds || candidate.supported_payload_kinds.includes('schema_validated')) && (
            <StyledButton
              variant="subtle"
              size="sm"
              onClick={() => appendTemplate('schema_validated', candidate)}
            >
              Add schema-valid
            </StyledButton>
          )}
          <StyledButton
            variant="subtle"
            size="sm"
            onClick={() => appendTemplate(
              candidate.supported_payload_kinds?.includes('producer_validated') ? 'producer_validated' : 'opaque_json',
              candidate,
            )}
          >
            {candidate.supported_payload_kinds?.includes('producer_validated') ? 'Add producer-valid value' : 'Add value'}
          </StyledButton>
        </Group>
      </Group>
    ))}
    {allowConfiguredInputs && ineligibleInputs.map((input) => (
      <Alert
        key={`${input.runtime_step_id}:${input.input_group_key}:${input.input_index}`}
        color="yellow"
        title={`${input.step_name} · ${input.input_group_key}[${input.input_index}] is unavailable`}
      >
        {input.message}
      </Alert>
    ))}
    {allowConfiguredInputs && !isLoading && !error && candidates.length === 0 && (
      <Text size="xs" c="dimmed">No eligible configured JSON or tabular inputs found.</Text>
    )}
    {allowConfiguredInputs && (
      <Textarea
        label="Synthetic input overrides JSON"
        description="Use an eligible input slot. Producer-valid values must match the source representation and any available schema. Preserve table headers and row order. A synthetic response does not prove a provider call succeeded."
        value={inputOverridesValue}
        onChange={(event) => onInputOverridesChange(event.currentTarget.value)}
        autosize
        minRows={8}
        styles={{ input: { fontFamily: 'monospace' } }}
      />
    )}
    <Text fw={600} size="sm">Schema-backed Manual Trigger inputs</Text>
    {triggerCandidates.map((candidate) => (
      <Group key={`${candidate.trigger_step_id}:${candidate.trigger_id}`} justify="space-between">
        <Text size="xs" c="dimmed">
          {candidate.trigger_step_name} · {candidate.input_name}
          {' · '}<Code>{candidate.trigger_id}</Code>
          {' · '}schema <Code>{candidate.schema_id} v{candidate.schema_version}</Code>
          {candidate.is_required ? ' · required' : ' · optional'}
        </Text>
        <StyledButton
          variant="subtle"
          size="sm"
          onClick={() => appendTriggerTemplate(candidate)}
        >
          Add trigger value
        </StyledButton>
      </Group>
    ))}
    {ineligibleTriggerInputs.map((input) => (
      <Alert
        key={`${input.trigger_step_id}:${input.trigger_id}`}
        color="yellow"
        title={`${input.trigger_step_name} · ${input.input_name} is unavailable`}
      >
        {input.message}
      </Alert>
    ))}
    {!isLoading && !error && triggerCandidates.length === 0 && (
      <Text size="xs" c="dimmed">No eligible schema-backed Manual Trigger JSON inputs found.</Text>
    )}
    <Textarea
      label="Manual Trigger overrides JSON"
      description="Use an eligible trigger step and trigger input identity. Values must satisfy the exact configured JSON schema."
      value={triggerOverridesValue}
      onChange={(event) => onTriggerOverridesChange(event.currentTarget.value)}
      autosize
      minRows={6}
      styles={{ input: { fontFamily: 'monospace' } }}
    />
    <Checkbox
      label="I confirm these synthetic values contain no secrets or sensitive customer data."
      checked={confirmedNonSensitive}
      onChange={(event) => onConfirmedNonSensitiveChange(event.currentTarget.checked)}
    />
  </Stack>;
}
